import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  buildInventoryAttestedLotCommandV1,
} from '../../lib/warehouse/inventoryAttestedLot.js';
import { normalizeComparableUom } from '@trackeat/shared';
import {
  alertCircleOutline,
  documentTextOutline,
  lockClosedOutline,
  refreshOutline,
} from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { hasAppCapability } from '../../lib/auth/capabilities.js';
import {
  useEnqueueConsultantHistoricalLotRecount,
  useEnqueueStockCommand,
  useEvents,
  useFiscalDocs,
  useMasters,
  useStockProjection,
} from '../../lib/data/queries.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  HISTORICAL_LOT_EPSILON,
  selectHistoricalLotCandidates,
  type HistoricalLotCandidate,
  type HistoricalLotSourceEvent,
} from '../../lib/warehouse/historicalLotCandidates.js';
import type {
  HistoricalLotRecountConversionSource,
} from '../../lib/warehouse/historicalLotRecount.js';
import {
  consultantHistoricalLotRecountScopeKey,
} from '../../lib/warehouse/historicalLotRecount.js';

interface FoodRow {
  id: string;
  name: string;
  default_uom: string;
  archived_at?: string | null;
}

interface SupplierRow {
  id: string;
  name: string;
  archived_at?: string | null;
}

interface DocumentRow {
  id: string;
  document_number?: string | null;
  document_date?: string | null;
  supplier_id?: string | null;
  archived_at?: string | null;
}

interface RecountForm {
  sourceQty: string;
  sourceUom: string;
  conversionFactor: string;
  note: string;
}

interface InventoryLotForm {
  foodId: string;
  batchLot: string;
  inventoryDate: string;
  expirationDate: string;
  sourceQty: string;
  sourceUom: string;
  conversionFactor: string;
  inventoryReference: string;
  note: string;
}

const EMPTY_INVENTORY_LOT_FORM: InventoryLotForm = {
  foodId: '',
  batchLot: '',
  inventoryDate: new Date().toISOString().slice(0, 10),
  expirationDate: '',
  sourceQty: '',
  sourceUom: 'cartoni',
  conversionFactor: '',
  inventoryReference: '',
  note: '',
};

const CARD_STYLE: React.CSSProperties = {
  margin: '12px 16px',
  borderRadius: 14,
  background: '#fff',
  boxShadow: '0 1px 2px rgba(15,23,42,.08), 0 3px 8px rgba(15,23,42,.05)',
  overflow: 'hidden',
};

const INFO_STYLE: React.CSSProperties = {
  margin: '12px 16px',
  padding: 14,
  borderRadius: 12,
  background: '#eff6ff',
  color: '#1e3a5f',
  fontSize: 13,
  lineHeight: 1.45,
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function cleanText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim();
  return clean || null;
}

function parseQuantity(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function inventoryDateToIso(value: string): string | null {
  const date = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const parsed = new Date(`${date}T12:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date
    ? null
    : parsed.toISOString();
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    maximumFractionDigits: 3,
  }).format(value);
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = new Date(value + 'T00:00:00');
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
}

function metricFactor(from: string, to: string): number | null {
  const normalizedFrom = normalizeComparableUom(from);
  const normalizedTo = normalizeComparableUom(to);
  const mass: Record<string, number> = { kg: 1, g: 0.001 };
  const volume: Record<string, number> = { l: 1, ml: 0.001 };
  if (mass[normalizedFrom] != null && mass[normalizedTo] != null) {
    return mass[normalizedFrom] / mass[normalizedTo];
  }
  if (volume[normalizedFrom] != null && volume[normalizedTo] != null) {
    return volume[normalizedFrom] / volume[normalizedTo];
  }
  return null;
}

function conversionSourceFor(
  sourceUom: string,
  stockUom: string,
  factor: number,
): HistoricalLotRecountConversionSource {
  const source = normalizeComparableUom(sourceUom);
  const stock = normalizeComparableUom(stockUom);
  if (source === stock && Math.abs(factor - 1) <= 1e-9) return 'identity';
  const metric = metricFactor(source, stock);
  if (metric != null && Math.abs(metric - factor) <= 1e-9 * Math.max(1, metric, factor)) {
    return 'metric';
  }
  return 'user_confirmed';
}

function initialForm(candidate: HistoricalLotCandidate): RecountForm {
  const carton = candidate.source.cartonConversion;
  if (carton) {
    return {
      sourceQty: '',
      sourceUom: 'collo',
      conversionFactor: String(carton.factorPerCarton),
      note: '',
    };
  }
  return {
    sourceQty: '',
    sourceUom: candidate.balance.uom,
    conversionFactor: '1',
    note: '',
  };
}

function candidateCanRecover(
  candidate: HistoricalLotCandidate,
  food: FoodRow | undefined,
  recountAlreadyQueued = false,
): boolean {
  return !recountAlreadyQueued
    && food !== undefined
    && !food.archived_at
    && Math.abs(candidate.balance.onHandQty) <= HISTORICAL_LOT_EPSILON
    && Math.abs(candidate.balance.reservedQty ?? 0) <= HISTORICAL_LOT_EPSILON
    && candidate.expectedBalance.event_count > 0
    && candidate.expectedBalance.last_event_client_uuid !== null
    && UUID_RE.test(candidate.expectedBalance.last_event_client_uuid);
}

function candidateRecountScopeKey(
  candidate: HistoricalLotCandidate,
  businessId: string | null,
): string | null {
  const lastEventClientUuid = candidate.expectedBalance.last_event_client_uuid;
  if (
    businessId === null
    || lastEventClientUuid === null
    || !UUID_RE.test(lastEventClientUuid)
  ) {
    return null;
  }
  return consultantHistoricalLotRecountScopeKey({
    businessId,
    lastEventClientUuid,
  });
}

/**
 * Deep-link guard. The same owner-only rule is repeated in the server RPC;
 * this page is intentionally not the authorization boundary.
 */
export function HistoricalLotsPage(): JSX.Element {
  const { profile } = useAuth();
  if (!hasAppCapability(profile, 'warehouse.historical_lots')) {
    return <Redirect to="/warehouse" />;
  }
  return <HistoricalLotsContent />;
}

function HistoricalLotsContent(): JSX.Element {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<HistoricalLotCandidate | null>(null);
  const [form, setForm] = useState<RecountForm | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [inventoryConfirmationOpen, setInventoryConfirmationOpen] = useState(false);
  const [inventoryForm, setInventoryForm] = useState<InventoryLotForm>(
    EMPTY_INVENTORY_LOT_FORM,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [recountScopesQueued, setRecountScopesQueued] = useState<Set<string>>(
    () => new Set(),
  );
  const recount = useEnqueueConsultantHistoricalLotRecount();
  const inventoryAttested = useEnqueueStockCommand();

  const { data: foods = [], isLoading: foodsLoading } = useMasters<FoodRow>(
    'foods',
    { includeArchived: true },
  );
  const { data: suppliers = [] } = useMasters<SupplierRow>(
    'suppliers',
    { includeArchived: true },
  );
  const { data: documents = [] } = useMasters<DocumentRow>(
    'documents',
    { includeArchived: true },
  );
  // DDT live in the local `documents` master; invoice imports retain their
  // immutable metadata in the fiscal archive. The latter is online-only and
  // deliberately not a loading blocker, so an offline consultant still sees
  // the safe server-confirmed source with a UUID fallback.
  const { data: fiscalDocuments = [] } = useFiscalDocs();
  const { data: stockInEvents = [], isLoading: stockInLoading } = useEvents(
    'stock_in',
    null,
  );
  const { data: stockOutEvents = [], isLoading: stockOutLoading } = useEvents(
    'stock_out',
    null,
  );

  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => cleanText(food.default_uom) !== null)
        .map((food) => [food.id, food.default_uom]),
    ),
    [foods],
  );
  const stock = useStockProjection({ canonicalUomByFood });
  const activeBusinessId = profile?.active_business_id ?? null;
  const activeTenantId = profile?.active_tenant_id ?? null;

  const candidates = useMemo(
    () => selectHistoricalLotCandidates({
      balances: stock.data.balances,
      stockInEvents: stockInEvents as HistoricalLotSourceEvent[],
      stockOutEvents: stockOutEvents as HistoricalLotSourceEvent[],
      activeBusinessId,
    }),
    [
      activeBusinessId,
      stock.data.balances,
      stockInEvents,
      stockOutEvents,
    ],
  );
  const foodById = useMemo(
    () => new Map(foods.map((food) => [food.id, food])),
    [foods],
  );
  const inventoryFoods = useMemo(
    () => foods
      .filter((food) => !food.archived_at && cleanText(food.default_uom) !== null)
      .sort((left, right) => left.name.localeCompare(right.name, 'it')),
    [foods],
  );
  const inventoryFood = inventoryForm.foodId
    ? foodById.get(inventoryForm.foodId)
    : undefined;
  const inventorySourceQty = parseQuantity(inventoryForm.sourceQty);
  const inventoryFactor = parseQuantity(inventoryForm.conversionFactor);
  const inventoryStockQty = inventorySourceQty != null && inventoryFactor != null
    ? inventorySourceQty * inventoryFactor
    : null;
  const inventoryCountedAt = inventoryDateToIso(inventoryForm.inventoryDate);
  const inventoryExistingBalance = useMemo(() => {
    const lot = inventoryForm.batchLot.trim();
    if (!inventoryForm.foodId || !lot) return undefined;
    return stock.data.balances.find((balance) =>
      balance.foodId === inventoryForm.foodId
      && (balance.batchLot ?? '').trim() === lot,
    );
  }, [inventoryForm.batchLot, inventoryForm.foodId, stock.data.balances]);
  const inventoryExistingPositive = inventoryExistingBalance !== undefined
    && Math.abs(inventoryExistingBalance.onHandQty) > HISTORICAL_LOT_EPSILON;
  const inventoryExistingClosed = inventoryExistingBalance?.closed === true;
  const inventoryFormValid = !!(
    inventoryFood
    && cleanText(inventoryForm.batchLot)
    && inventorySourceQty != null
    && inventoryFactor != null
    && inventoryStockQty != null
    && inventoryCountedAt !== null
    && cleanText(inventoryForm.sourceUom)
    && cleanText(inventoryForm.inventoryReference)
    && !inventoryExistingPositive
    && !inventoryExistingClosed
  );
  const supplierById = useMemo(
    () => new Map(suppliers.map((supplier) => [supplier.id, supplier])),
    [suppliers],
  );
  const documentById = useMemo(
    () => new Map(documents.map((document) => [document.id, document])),
    [documents],
  );
  const fiscalDocumentById = useMemo(
    () => new Map(fiscalDocuments.map((document) => [document.id, document])),
    [fiscalDocuments],
  );
  const filteredCandidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return candidates;
    return candidates.filter((candidate) => {
      const food = foodById.get(candidate.foodId)?.name ?? candidate.foodId;
      const document = documentById.get(candidate.documentId)?.document_number
        ?? fiscalDocumentById.get(candidate.documentId)?.number
        ?? cleanText(candidate.source.event.payload?.document_number)
        ?? candidate.documentId;
      return [food, candidate.batchLot, document]
        .some((value) => value.toLowerCase().includes(needle));
    });
  }, [candidates, documentById, fiscalDocumentById, foodById, search]);

  const sourceQty = form ? parseQuantity(form.sourceQty) : null;
  const conversionFactor = form ? parseQuantity(form.conversionFactor) : null;
  const countedStockQty = sourceQty != null && conversionFactor != null
    ? sourceQty * conversionFactor
    : null;
  const selectedScopeKey = selected
    ? candidateRecountScopeKey(selected, activeBusinessId)
    : null;
  const selectedCanRecover = selected
    ? candidateCanRecover(
      selected,
      foodById.get(selected.foodId),
      selectedScopeKey !== null && recountScopesQueued.has(selectedScopeKey),
    )
    : false;
  const formValid = !!(
    selected
    && form
    && selectedCanRecover
    && sourceQty != null
    && conversionFactor != null
    && countedStockQty != null
    && cleanText(form.sourceUom)
    && cleanText(form.note)
  );

  const openRecovery = (candidate: HistoricalLotCandidate): void => {
    const scopeKey = candidateRecountScopeKey(candidate, activeBusinessId);
    if (scopeKey !== null && recountScopesQueued.has(scopeKey)) {
      setToast('Una rettifica per questo stesso saldo è già in coda. Sincronizza per vederne l’esito prima di inserirne un’altra.');
      return;
    }
    setSelected(candidate);
    setForm(initialForm(candidate));
  };

  const closeRecovery = (): void => {
    if (recount.isPending) return;
    setConfirmationOpen(false);
    setSelected(null);
    setForm(null);
  };

  const closeInventory = (): void => {
    if (inventoryAttested.isPending) return;
    setInventoryConfirmationOpen(false);
    setInventoryOpen(false);
    setInventoryForm(EMPTY_INVENTORY_LOT_FORM);
  };

  const submitInventory = async (): Promise<void> => {
    if (
      !inventoryFormValid
      || !inventoryFood
      || !activeTenantId
      || !activeBusinessId
      || inventorySourceQty == null
      || inventoryFactor == null
      || inventoryCountedAt === null
    ) {
      setInventoryConfirmationOpen(false);
      setToast('Completa prodotto, lotto, quantità, fattore e riferimento inventario.');
      return;
    }
    try {
      const command = buildInventoryAttestedLotCommandV1({
        commandId: crypto.randomUUID(),
        tenantId: activeTenantId,
        businessId: activeBusinessId,
        foodId: inventoryFood.id,
        batchLot: inventoryForm.batchLot,
        expirationDate: cleanText(inventoryForm.expirationDate),
        stockUom: inventoryFood.default_uom,
        countedSourceQty: inventorySourceQty,
        countedSourceUom: inventoryForm.sourceUom,
        conversionFactor: inventoryFactor,
        inventoryDate: inventoryForm.inventoryDate,
        inventoryReference: inventoryForm.inventoryReference,
        note: inventoryForm.note,
        countedAt: inventoryCountedAt,
        operatorName: profile?.full_name?.trim() || 'Consulente piattaforma',
      });
      await inventoryAttested.mutateAsync(command);
      closeInventory();
      setToast(
        'Lotto da inventario messo in coda protetta. Comparirà nel magazzino come “inventario attestato”, senza creare un DDT o un acquisto.',
      );
    } catch (error) {
      setInventoryConfirmationOpen(false);
      setToast(toUserMessage(error, 'Impossibile registrare il lotto da inventario'));
    }
  };

  const submitRecovery = async (): Promise<void> => {
    if (
      !selected
      || !form
      || !formValid
      || !activeTenantId
      || !activeBusinessId
      || sourceQty == null
      || conversionFactor == null
      || countedStockQty == null
      || selected.expectedBalance.last_event_client_uuid == null
    ) {
      setConfirmationOpen(false);
      setToast('Completa quantità, unità, fattore e motivo prima di confermare.');
      return;
    }

    try {
      const sourceUom = form.sourceUom.trim().toLowerCase();
      await recount.mutateAsync({
        requestId: crypto.randomUUID(),
        tenantId: activeTenantId,
        businessId: activeBusinessId,
        sourceEventId: selected.sourceEventId,
        expectedBalance: {
          qty: selected.expectedBalance.qty,
          event_count: selected.expectedBalance.event_count,
          last_event_client_uuid: selected.expectedBalance.last_event_client_uuid,
          closed: selected.expectedBalance.closed,
        },
        countedStockQty,
        stockUom: selected.balance.uom,
        countedSourceQty: sourceQty,
        countedSourceUom: sourceUom,
        conversionFactor,
        conversionSource: conversionSourceFor(
          sourceUom,
          selected.balance.uom,
          conversionFactor,
        ),
        countedAt: new Date().toISOString(),
        operatorName: profile?.full_name?.trim() || 'Consulente piattaforma',
        note: form.note.trim(),
      });
      if (selectedScopeKey !== null) {
        setRecountScopesQueued((current) => {
          const next = new Set(current);
          next.add(selectedScopeKey);
          return next;
        });
      }
      setConfirmationOpen(false);
      setSelected(null);
      setForm(null);
      setToast(
        'Conteggio messo nella coda protetta. DDT e storico restano invariati; il server verificherà saldo, documento e prenotazioni prima di riaprire il lotto.',
      );
      await queryClient.invalidateQueries({ queryKey: ['events'] });
    } catch (error) {
      setConfirmationOpen(false);
      setToast(toUserMessage(error, 'Impossibile registrare la rettifica protetta'));
    }
  };

  const loading = foodsLoading || stockInLoading || stockOutLoading || stock.isLoading;

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/warehouse" />
          </IonButtons>
          <IonTitle>Lotti storici</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color="light">
        <div style={INFO_STYLE}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <IonIcon icon={lockClosedOutline} style={{ flex: '0 0 auto', marginTop: 2 }} />
            <div>
              <strong>Area riservata al consulente piattaforma</strong>
              <br />
              Qui compaiono lotti chiusi o a giacenza zero. La rettifica aggiunge
              un nuovo fatto tracciato: non modifica né annulla DDT, fattura o
              movimenti precedenti.
            </div>
          </div>
        </div>

        <IonSearchbar
          value={search}
          debounce={180}
          placeholder="Cerca prodotto, lotto o DDT"
          onIonInput={(event) => setSearch(String(event.detail.value ?? ''))}
        />

        <div style={{ ...CARD_STYLE, padding: 14, color: '#1e3a5f' }}>
          <strong>Hai un inventario fisico con lotti non ancora presenti?</strong>
          <p style={{ margin: '6px 0 10px', fontSize: 13, lineHeight: 1.45 }}>
            Registrali qui come inventario attestato. Non verrà creato un DDT,
            un fornitore o un costo fittizio: resteranno lotto, quantità,
            conversione e riferimento dell’inventario.
          </p>
          <IonButton
            expand="block"
            style={{ minHeight: 44, margin: 0, textTransform: 'none' }}
            onClick={() => setInventoryOpen(true)}
          >
            Registra lotto da inventario
          </IonButton>
        </div>

        {loading && (
          <div className="ion-text-center" style={{ padding: 32 }}>
            <IonSpinner name="crescent" />
            <p style={{ color: '#64748b', marginTop: 8 }}>Sto verificando lo storico dei lotti…</p>
          </div>
        )}

        {!loading && filteredCandidates.length === 0 && (
          <div style={{ ...CARD_STYLE, padding: 20, color: '#475569' }}>
            <strong>Nessun lotto storico documentato da mostrare.</strong>
            <p style={{ marginBottom: 0, fontSize: 13, lineHeight: 1.45 }}>
              Sono esclusi i carichi ancora solo in coda e i lotti aperti con
              giacenza disponibile. Se il lotto compare soltanto nell’inventario
              fisico, usa “Registra lotto da inventario”: non è un DDT.
            </p>
          </div>
        )}

        {!loading && filteredCandidates.map((candidate) => {
          const food = foodById.get(candidate.foodId);
          const document = documentById.get(candidate.documentId);
          const fiscalDocument = fiscalDocumentById.get(candidate.documentId);
          const supplier = candidate.supplierId
            ? supplierById.get(candidate.supplierId)
            : undefined;
          const documentNumber = document?.document_number
            ?? fiscalDocument?.number
            ?? cleanText(candidate.source.event.payload?.document_number)
            ?? candidate.documentId;
          const documentDate = document?.document_date
            ?? fiscalDocument?.date
            ?? cleanText(candidate.source.event.payload?.document_date);
          const recountScopeKey = candidateRecountScopeKey(
            candidate,
            activeBusinessId,
          );
          const recountAlreadyQueued = recountScopeKey !== null
            && recountScopesQueued.has(recountScopeKey);
          const canRecover = candidateCanRecover(
            candidate,
            food,
            recountAlreadyQueued,
          );
          const carton = candidate.source.cartonConversion;
          const title = food?.name ?? ('Prodotto storico ' + candidate.foodId.slice(0, 8));
          const state = candidate.balance.closed ? 'Lotto chiuso' : 'Giacenza zero';
          const dateLabel = formatDate(documentDate);
          const expiryLabel = formatDate(candidate.expirationDate);
          return (
            <article key={candidate.sourceEventId} style={CARD_STYLE}>
              <div style={{ padding: '14px 14px 10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <strong style={{ color: '#0f172a' }}>{title}</strong>
                    <div style={{ color: '#334155', marginTop: 4 }}>
                      Lotto <strong>{candidate.batchLot}</strong> · {state}
                    </div>
                  </div>
                  <span
                    style={{
                      alignSelf: 'flex-start',
                      borderRadius: 999,
                      padding: '3px 8px',
                      color: candidate.balance.closed ? '#92400e' : '#075985',
                      background: candidate.balance.closed ? '#fef3c7' : '#e0f2fe',
                      fontSize: 12,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {candidate.balance.closed ? 'CHIUSO' : 'ZERO'}
                  </span>
                </div>
                <IonList lines="none" style={{ marginTop: 8, marginBottom: 0 }}>
                  <IonItem style={{ '--padding-start': 0, '--inner-padding-end': 0 } as React.CSSProperties}>
                    <IonLabel className="ion-text-wrap">
                      <p style={{ margin: '0 0 4px' }}>
                        <IonIcon icon={documentTextOutline} style={{ verticalAlign: 'text-bottom', marginRight: 5 }} />
                        DDT/fattura: <strong>{documentNumber}</strong>
                        {dateLabel ? ' · ' + dateLabel : ''}
                      </p>
                      <p style={{ margin: '0 0 4px' }}>
                        Fornitore: {supplier?.name ?? 'come da documento sorgente'}
                      </p>
                      <p style={{ margin: 0 }}>
                        Scadenza: {expiryLabel ?? 'non registrata'}
                        {' · '}Saldo: {formatQuantity(candidate.balance.onHandQty)} {candidate.balance.uom}
                      </p>
                      {carton && (
                        <p style={{ margin: '6px 0 0', color: '#475569' }}>
                          Conversione originaria: 1 collo = {formatQuantity(carton.factorPerCarton)} {carton.balanceUom}
                        </p>
                      )}
                    </IonLabel>
                  </IonItem>
                </IonList>
              </div>
              <div style={{ display: 'flex', padding: '0 10px 10px', gap: 8 }}>
                {canRecover ? (
                  <IonButton
                    expand="block"
                    color="primary"
                    style={{ flex: 1, minHeight: 44, textTransform: 'none' }}
                    onClick={() => openRecovery(candidate)}
                  >
                    Rettifica da conteggio fisico
                  </IonButton>
                ) : (
                  <IonNote className="ion-text-wrap" style={{ padding: '0 4px 4px', color: '#9a3412' }}>
                    {recountAlreadyQueued
                      ? 'Una rettifica per questo stesso saldo è già in coda: sincronizza per verificarne l’esito prima di inserirne un’altra.'
                      : food === undefined
                      ? 'Mancano i dati aggiornati del prodotto: sincronizza prima di intervenire.'
                      : food.archived_at
                        ? 'Il prodotto è archiviato: il lotto resta consultabile ma non può essere riaperto.'
                      : Math.abs(candidate.balance.reservedQty ?? 0) > HISTORICAL_LOT_EPSILON
                      ? 'Ci sono quantità prenotate: rilascia o verifica prima la prenotazione.'
                      : Math.abs(candidate.balance.onHandQty) > HISTORICAL_LOT_EPSILON
                        ? 'Il lotto è chiuso ma ha un saldo: verifica la discrepanza prima della rettifica.'
                        : 'Manca l’identità completa dell’ultimo evento: completa la sincronizzazione prima di intervenire.'}
                  </IonNote>
                )}
              </div>
            </article>
          );
        })}

        <IonModal isOpen={selected !== null} onDidDismiss={closeRecovery}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Conteggio fisico</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={closeRecovery} disabled={recount.isPending}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent color="light">
            {selected && form && (
              <>
                <div style={INFO_STYLE}>
                  <strong>{foodById.get(selected.foodId)?.name ?? 'Prodotto storico'}</strong>
                  <br />
                  Lotto {selected.batchLot} · saldo sistema {formatQuantity(selected.balance.onHandQty)} {selected.balance.uom}
                  <br />
                  Inserisci solo ciò che hai contato fisicamente. Il fattore deve
                  essere quello verificato sull’imballo o sul documento.
                </div>
                <IonList inset>
                  <IonItem>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      label="Quantità fisica trovata *"
                      labelPlacement="stacked"
                      placeholder="Es. 12"
                      value={form.sourceQty}
                      onIonInput={(event) => setForm((current) => current && ({
                        ...current,
                        sourceQty: String(event.detail.value ?? ''),
                      }))}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      label="Unità fisica *"
                      labelPlacement="stacked"
                      placeholder="Es. collo, cartone, kg, pz"
                      value={form.sourceUom}
                      maxlength={10}
                      onIonInput={(event) => setForm((current) => current && ({
                        ...current,
                        sourceUom: String(event.detail.value ?? ''),
                      }))}
                    />
                  </IonItem>
                  <IonItem>
                    <IonInput
                      type="number"
                      inputmode="decimal"
                      label={'Fattore in ' + selected.balance.uom + ' *'}
                      labelPlacement="stacked"
                      placeholder="Es. 5,3 oppure 80"
                      value={form.conversionFactor}
                      onIonInput={(event) => setForm((current) => current && ({
                        ...current,
                        conversionFactor: String(event.detail.value ?? ''),
                      }))}
                    />
                  </IonItem>
                  <IonItem>
                    <IonTextarea
                      autoGrow
                      label="Motivo del conteggio *"
                      labelPlacement="stacked"
                      placeholder="Es. Cartoni ritrovati e ricontati fisicamente in cella."
                      value={form.note}
                      maxlength={500}
                      onIonInput={(event) => setForm((current) => current && ({
                        ...current,
                        note: String(event.detail.value ?? ''),
                      }))}
                    />
                  </IonItem>
                </IonList>

                <div style={{ ...INFO_STYLE, background: '#f0fdf4', color: '#14532d' }}>
                  <strong>Effetto proposto</strong>
                  <br />
                  {sourceQty != null && conversionFactor != null && countedStockQty != null
                    ? formatQuantity(sourceQty) + ' ' + (form.sourceUom.trim() || 'unità')
                      + ' × ' + formatQuantity(conversionFactor) + ' = '
                      + formatQuantity(countedStockQty) + ' ' + selected.balance.uom
                    : 'Inserisci quantità e fattore per calcolare la giacenza da registrare.'}
                  <br />
                  <span style={{ fontSize: 12 }}>
                    Il server ricontrollerà il saldo zero, il DDT sorgente e le prenotazioni prima di accettare.
                  </span>
                </div>

                <div style={{ padding: '0 16px 24px' }}>
                  <IonButton
                    expand="block"
                    disabled={!formValid || recount.isPending}
                    style={{ minHeight: 48, textTransform: 'none' }}
                    onClick={() => setConfirmationOpen(true)}
                  >
                    {recount.isPending ? <IonSpinner name="crescent" /> : 'Verifica e registra rettifica'}
                  </IonButton>
                </div>
              </>
            )}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={confirmationOpen}
          onDidDismiss={() => setConfirmationOpen(false)}
          header="Confermi il conteggio fisico?"
          message={selected && countedStockQty != null
            ? 'Verrà aggiunta una rettifica di ' + formatQuantity(countedStockQty)
              + ' ' + selected.balance.uom + ' sul lotto ' + selected.batchLot
              + '. Il DDT e i movimenti esistenti non verranno modificati.'
            : 'Verifica i dati inseriti.'}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Conferma',
              role: 'confirm',
              handler: () => { void submitRecovery(); },
            },
          ]}
        />

        <IonModal isOpen={inventoryOpen} onDidDismiss={closeInventory}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Lotto da inventario</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={closeInventory} disabled={inventoryAttested.isPending}>
                  Chiudi
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent color="light">
            <div style={INFO_STYLE}>
              <strong>Inventario attestato, non acquisto</strong>
              <br />
              Inserisci il lotto che hai rilevato fisicamente. Il sistema non lo
              collegherà a un DDT inesistente e non gli attribuirà costi o fornitore.
            </div>
            <IonList inset>
              <IonItem>
                <IonSelect
                  label="Prodotto esistente *"
                  labelPlacement="stacked"
                  placeholder="Scegli prodotto"
                  value={inventoryForm.foodId}
                  onIonChange={(event) => setInventoryForm((current) => ({
                    ...current,
                    foodId: String(event.detail.value ?? ''),
                  }))}
                >
                  {inventoryFoods.map((food) => (
                    <IonSelectOption key={food.id} value={food.id}>
                      {food.name} · {food.default_uom}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              <IonItem>
                <IonInput
                  label="Numero lotto *"
                  labelPlacement="stacked"
                  placeholder="Es. 409046 oppure 180/26"
                  value={inventoryForm.batchLot}
                  maxlength={100}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    batchLot: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  type="date"
                  label="Data dell'inventario *"
                  labelPlacement="stacked"
                  value={inventoryForm.inventoryDate}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    inventoryDate: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  type="date"
                  label="Scadenza (se rilevata)"
                  labelPlacement="stacked"
                  value={inventoryForm.expirationDate}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    expirationDate: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  label="Quantità fisica *"
                  labelPlacement="stacked"
                  placeholder="Es. 12"
                  value={inventoryForm.sourceQty}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    sourceQty: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  label="Unità fisica *"
                  labelPlacement="stacked"
                  placeholder="Es. cartoni, kg, pz"
                  value={inventoryForm.sourceUom}
                  maxlength={10}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    sourceUom: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  type="number"
                  inputmode="decimal"
                  label={'Fattore in ' + (inventoryFood?.default_uom ?? 'UdM magazzino') + ' *'}
                  labelPlacement="stacked"
                  placeholder="Es. 5,3 oppure 80"
                  value={inventoryForm.conversionFactor}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    conversionFactor: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  label="Riferimento inventario *"
                  labelPlacement="stacked"
                  placeholder="Es. Inventario Le Boccerie 08/08/2026"
                  value={inventoryForm.inventoryReference}
                  maxlength={80}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    inventoryReference: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
              <IonItem>
                <IonTextarea
                  autoGrow
                  label="Nota (facoltativa)"
                  labelPlacement="stacked"
                  placeholder="Es. Cartoni ricontati in cella."
                  value={inventoryForm.note}
                  maxlength={60}
                  onIonInput={(event) => setInventoryForm((current) => ({
                    ...current,
                    note: String(event.detail.value ?? ''),
                  }))}
                />
              </IonItem>
            </IonList>

            <div style={{ ...INFO_STYLE, background: '#f0fdf4', color: '#14532d' }}>
              <strong>Giacenza che verrà registrata</strong>
              <br />
              {inventorySourceQty != null && inventoryFactor != null && inventoryStockQty != null
                ? formatQuantity(inventorySourceQty) + ' '
                  + (inventoryForm.sourceUom.trim() || 'unità') + ' × '
                  + formatQuantity(inventoryFactor) + ' = '
                  + formatQuantity(inventoryStockQty) + ' '
                  + (inventoryFood?.default_uom ?? 'UdM magazzino')
                : 'Inserisci quantità e fattore per calcolare la giacenza.'}
              <br />
              <span style={{ fontSize: 12 }}>
                Solo il platform owner può confermarla. Il server rifiuta un lotto già aperto con quantità disponibile.
              </span>
            </div>

            {(inventoryExistingPositive || inventoryExistingClosed) && (
              <IonNote className="ion-text-wrap" style={{ display: 'block', margin: '0 16px 12px', color: '#9a3412' }}>
                {inventoryExistingPositive
                  ? 'Questo lotto ha già una giacenza disponibile: correggilo dal dettaglio magazzino, senza creare un secondo carico.'
                  : 'Questo lotto è già chiuso: usa la rettifica storica documentata, non un nuovo inventario.'}
              </IonNote>
            )}

            <div style={{ padding: '0 16px 24px' }}>
              <IonButton
                expand="block"
                disabled={!inventoryFormValid || inventoryAttested.isPending}
                style={{ minHeight: 48, textTransform: 'none' }}
                onClick={() => setInventoryConfirmationOpen(true)}
              >
                {inventoryAttested.isPending ? <IonSpinner name="crescent" /> : 'Verifica e registra inventario'}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={inventoryConfirmationOpen}
          onDidDismiss={() => setInventoryConfirmationOpen(false)}
          header="Confermi il lotto da inventario?"
          message={inventoryFood && inventoryStockQty != null
            ? 'Verrà registrato ' + inventoryFood.name + ', lotto '
              + inventoryForm.batchLot.trim() + ', per ' + formatQuantity(inventoryStockQty)
              + ' ' + inventoryFood.default_uom
              + '. Non verrà creato né modificato alcun DDT, fornitore o costo.'
            : 'Verifica i dati inseriti.'}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            {
              text: 'Conferma',
              role: 'confirm',
              handler: () => { void submitInventory(); },
            },
          ]}
        />

        <IonToast
          isOpen={toast !== null}
          onDidDismiss={() => setToast(null)}
          message={toast ?? ''}
          duration={6000}
          position="bottom"
          color={toast?.startsWith('Impossibile') ? 'danger' : 'primary'}
          icon={toast?.startsWith('Impossibile') ? alertCircleOutline : refreshOutline}
        />
      </IonContent>
    </IonPage>
  );
}
